import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { navigate } from "@reach/router";
import { AuthContext } from "../helpers/AuthContext";
import Sidebar from "../components/Sidebar";
import "../styles/responsive.css";
import "../styles/results.css";

const AdminQuality = () => {
  const context = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // Filter + sort UI state
  const [search, setSearch] = useState("");
  const [minAgreement, setMinAgreement] = useState("");
  const [minLabels, setMinLabels] = useState("");
  const [sortKey, setSortKey] = useState("agreementPct");
  const [sortDir, setSortDir] = useState("asc"); // worst first by default
  const [adminsOnly, setAdminsOnly] = useState(false);

  // Promote-to-admin UI state
  const [promoteEmail, setPromoteEmail] = useState("");
  const [promoteStatus, setPromoteStatus] = useState(null); // { ok, message }
  const [promoting, setPromoting] = useState(false);

  // Privacy toggle: whether the results modal shows full labeler names
  const [showLabelerNames, setShowLabelerNames] = useState(true);
  const [savingNamesToggle, setSavingNamesToggle] = useState(false);

  const load = (endpoint = "overview", method = "get") => {
    setLoading(true);
    setError("");
    const headers = context.authHeader ? context.authHeader() : {};
    const req =
      method === "post"
        ? axios.post(`/api/admin/${endpoint}`, {}, { headers })
        : axios.get(`/api/admin/${endpoint}`, { headers });
    return req
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Request failed");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (context.Auth !== "Admin") return;
    load();
    axios
      .get("/api/admin/settings", {
        headers: context.authHeader ? context.authHeader() : {},
      })
      .then((res) => {
        if (typeof res.data?.showLabelerNames === "boolean") {
          setShowLabelerNames(res.data.showLabelerNames);
        }
      })
      .catch(() => {
        /* ignore — toggle just stays at default */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.Auth]);

  const handleToggleNames = () => {
    const next = !showLabelerNames;
    setSavingNamesToggle(true);
    axios
      .post(
        "/api/admin/settings",
        { showLabelerNames: next },
        { headers: context.authHeader ? context.authHeader() : {} }
      )
      .then((res) => {
        if (typeof res.data?.showLabelerNames === "boolean") {
          setShowLabelerNames(res.data.showLabelerNames);
        }
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message || err.message || "Failed to update setting"
        );
      })
      .finally(() => setSavingNamesToggle(false));
  };

  const handleRecompute = () => {
    setRecomputing(true);
    load("recompute-cllda", "post").finally(() => setRecomputing(false));
  };

  const callRoleEndpoint = (action) => {
    const email = promoteEmail.trim().toLowerCase();
    if (!email) {
      setPromoteStatus({ ok: false, message: "Enter an email." });
      return;
    }
    setPromoting(true);
    setPromoteStatus(null);
    axios
      .post(
        `/api/admin/${action}`,
        { email },
        { headers: context.authHeader ? context.authHeader() : {} }
      )
      .then((res) => {
        let msg;
        if (action === "promote") {
          msg = res.data.alreadyAdmin
            ? `${email} is already an admin.`
            : `${res.data.fname || email} is now an admin.`;
        } else {
          msg = res.data.wasAdmin
            ? `${res.data.fname || email} demoted to regular user.`
            : `${email} is not an admin.`;
        }
        setPromoteStatus({ ok: true, message: msg });
        setPromoteEmail("");
        load();
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          err.message ||
          `${action} request failed`;
        setPromoteStatus({ ok: false, message: msg });
      })
      .finally(() => setPromoting(false));
  };

  const handlePromote = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    callRoleEndpoint("promote");
  };

  const handleDemote = () => {
    if (!window.confirm(`Remove admin role from "${promoteEmail.trim()}"?`)) {
      return;
    }
    callRoleEndpoint("demote");
  };

  const handleDelete = (user, e) => {
    if (e) e.stopPropagation();
    const fullName = `${user.fname || ""} ${user.lname || ""}`.trim();
    if (
      !window.confirm(
        `Delete ${fullName || user.email}?\n\n` +
          `This permanently removes their account. Their existing labels stay in the dataset.`
      )
    ) {
      return;
    }
    axios
      .post(
        "/api/admin/delete-user",
        { email: user.email },
        { headers: context.authHeader ? context.authHeader() : {} }
      )
      .then(() => {
        load();
      })
      .catch((err) => {
        alert(
          err?.response?.data?.message || err.message || "Delete failed"
        );
      });
  };

  const sortedUsers = useMemo(() => {
    if (!data || !data.users) return [];
    const searchLower = search.trim().toLowerCase();
    const minAgreeNum = parseFloat(minAgreement);
    const minLabelsNum = parseFloat(minLabels);
    let rows = data.users.filter((u) => {
      if (searchLower) {
        const hay = `${u.fname || ""} ${u.lname || ""} ${u.email || ""} ${u.assoc || ""}`.toLowerCase();
        if (!hay.includes(searchLower)) return false;
      }
      if (!isNaN(minAgreeNum)) {
        if (u.agreementPct === null || u.agreementPct < minAgreeNum) return false;
      }
      if (!isNaN(minLabelsNum)) {
        if ((u.totalLabels || 0) < minLabelsNum) return false;
      }
      if (adminsOnly && !u.isAdmin) return false;
      return true;
    });
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // Nulls always sort to end
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [data, search, minAgreement, minLabels, sortKey, sortDir, adminsOnly]);

  const adminCount = useMemo(
    () => (data?.users || []).filter((u) => u.isAdmin).length,
    [data]
  );

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "agreementPct" ? "asc" : "desc");
    }
  };

  if (context.Auth !== "Admin") {
    return (
      <>
        <Sidebar />
        <div className="page-content">
          <div
            style={{
              maxWidth: 560,
              margin: "100px auto",
              padding: "30px 36px",
              border: "1px solid rgba(8, 145, 178, 0.28)",
              borderRadius: 16,
              background: "#fff",
              textAlign: "center",
            }}
          >
            <h2 style={{ fontFamily: "Poppins, sans-serif", color: "#0a0e1a" }}>
              Admin only
            </h2>
            <p style={{ color: "#4b5563" }}>
              You need an admin account to view the quality dashboard.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <div className="page-content">
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            padding: "100px 36px 60px",
            fontFamily: "Poppins, sans-serif",
            color: "#0a0e1a",
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: 22 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#0891b2",
                marginBottom: 6,
              }}
            >
              ● Admin // Quality Audit
            </div>
            <h1
              style={{
                fontSize: 36,
                fontWeight: 700,
                margin: 0,
                color: "#0a0e1a",
              }}
            >
              Labeler Quality Dashboard
            </h1>
            <p
              style={{
                fontSize: 14,
                color: "#4b5563",
                maxWidth: 680,
                marginTop: 6,
              }}
            >
              Per-user agreement with the CL-LDA consensus. Default sort shows
              the worst agreement first so outliers surface at the top.
              Click any row to drill into that labeler's full history.
            </p>
          </div>

          {/* Controls */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              marginBottom: 18,
              padding: "14px 18px",
              border: "1px solid rgba(8, 145, 178, 0.18)",
              borderRadius: 12,
              background: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <input
              type="text"
              placeholder="Search name / email / institution…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={inputStyle(260)}
            />
            <input
              type="number"
              placeholder="Min agreement %"
              value={minAgreement}
              onChange={(e) => setMinAgreement(e.target.value)}
              style={inputStyle(150)}
            />
            <input
              type="number"
              placeholder="Min labels"
              value={minLabels}
              onChange={(e) => setMinLabels(e.target.value)}
              style={inputStyle(130)}
            />
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 14px",
                border: "1px solid",
                borderColor: adminsOnly ? "#0891b2" : "rgba(8, 145, 178, 0.24)",
                borderRadius: 8,
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                color: adminsOnly ? "#0891b2" : "#4b5563",
                background: adminsOnly ? "rgba(8, 145, 178, 0.06)" : "#fff",
                cursor: "pointer",
                userSelect: "none",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              <input
                type="checkbox"
                checked={adminsOnly}
                onChange={(e) => setAdminsOnly(e.target.checked)}
                style={{ accentColor: "#0891b2" }}
              />
              Admins only ({adminCount})
            </label>
            <div style={{ flex: 1 }} />
            <button
              onClick={handleToggleNames}
              disabled={savingNamesToggle}
              title={
                showLabelerNames
                  ? "Currently SHOWING full labeler names in the results modal. Click to anonymize."
                  : "Currently HIDING labeler names (anonymous). Click to show names."
              }
              style={{
                background: showLabelerNames ? "#dc2626" : "#059669",
                color: "white",
                border: showLabelerNames
                  ? "1px solid #dc2626"
                  : "1px solid #059669",
                padding: "9px 16px",
                borderRadius: 8,
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: savingNamesToggle ? "not-allowed" : "pointer",
                opacity: savingNamesToggle ? 0.65 : 1,
                marginRight: 10,
              }}
            >
              {savingNamesToggle
                ? "Saving…"
                : showLabelerNames
                ? "Names: ON · click to hide"
                : "Names: OFF · click to show"}
            </button>
            <button
              onClick={handleRecompute}
              disabled={recomputing || loading}
              style={{
                background: "#0891b2",
                color: "white",
                border: "1px solid #0891b2",
                padding: "9px 20px",
                borderRadius: 8,
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                cursor: recomputing || loading ? "not-allowed" : "pointer",
                opacity: recomputing ? 0.65 : 1,
              }}
            >
              {recomputing ? "Running CL-LDA…" : "Recompute CL-LDA"}
            </button>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: 10,
                background: "rgba(220, 38, 38, 0.08)",
                border: "1px solid rgba(220, 38, 38, 0.3)",
                color: "#dc2626",
                marginBottom: 18,
              }}
            >
              {error}
            </div>
          )}

          {/* ===== Promote-to-admin panel ===== */}
          <form
            onSubmit={handlePromote}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 18,
              padding: "14px 18px",
              border: "1px solid rgba(217, 119, 6, 0.28)",
              borderRadius: 12,
              background: "rgba(217, 119, 6, 0.05)",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#d97706",
                marginRight: 4,
              }}
            >
              ● Grant Admin
            </div>
            <input
              type="email"
              placeholder="user@example.com"
              value={promoteEmail}
              onChange={(e) => setPromoteEmail(e.target.value)}
              style={{
                ...inputStyle(280),
                borderColor: "rgba(217, 119, 6, 0.35)",
              }}
            />
            <button
              type="submit"
              disabled={promoting}
              style={{
                background: "#d97706",
                color: "white",
                border: "1px solid #d97706",
                padding: "9px 20px",
                borderRadius: 8,
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                cursor: promoting ? "not-allowed" : "pointer",
                opacity: promoting ? 0.65 : 1,
              }}
            >
              {promoting ? "Working…" : "Grant admin"}
            </button>
            <button
              type="button"
              onClick={handleDemote}
              disabled={promoting}
              style={{
                background: "transparent",
                color: "#d97706",
                border: "1px solid rgba(217, 119, 6, 0.6)",
                padding: "9px 20px",
                borderRadius: 8,
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                cursor: promoting ? "not-allowed" : "pointer",
                opacity: promoting ? 0.65 : 1,
              }}
            >
              Revoke admin
            </button>
            {promoteStatus && (
              <div
                style={{
                  fontSize: 13,
                  color: promoteStatus.ok ? "#059669" : "#dc2626",
                  fontWeight: 500,
                }}
              >
                {promoteStatus.message}
              </div>
            )}
          </form>


          {/* Summary */}
          {data && (
            <div
              style={{
                display: "flex",
                gap: 16,
                marginBottom: 18,
                flexWrap: "wrap",
              }}
            >
              <StatCell label="Labelers" value={data.users.length} />
              <StatCell label="Components" value={data.totalComponents} />
              <StatCell
                label="Algorithm"
                value={data.clldaApplied ? "CL-LDA" : "Majority Vote"}
              />
            </div>
          )}

          {/* Table */}
          {loading && !data ? (
            <div style={{ padding: 40, textAlign: "center", color: "#4b5563" }}>
              Loading…
            </div>
          ) : (
            <div
              style={{
                background: "#fff",
                border: "1px solid rgba(8, 145, 178, 0.18)",
                borderRadius: 14,
                overflow: "hidden",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr style={headerRowStyle}>
                    <Th onClick={() => handleSort("fname")} sortKey={sortKey} dir={sortDir} thisKey="fname">Labeler</Th>
                    <Th onClick={() => handleSort("isAdmin")} sortKey={sortKey} dir={sortDir} thisKey="isAdmin" align="center">Role</Th>
                    <Th onClick={() => handleSort("assoc")} sortKey={sortKey} dir={sortDir} thisKey="assoc">Institution</Th>
                    <Th onClick={() => handleSort("totalLabels")} sortKey={sortKey} dir={sortDir} thisKey="totalLabels" align="right">Total Labels</Th>
                    <Th onClick={() => handleSort("uniqueComponents")} sortKey={sortKey} dir={sortDir} thisKey="uniqueComponents" align="right">Unique ICs</Th>
                    <Th onClick={() => handleSort("weight")} sortKey={sortKey} dir={sortDir} thisKey="weight" align="right">Weight</Th>
                    <Th onClick={() => handleSort("agreementPct")} sortKey={sortKey} dir={sortDir} thisKey="agreementPct" align="right">Agreement</Th>
                    <Th>Expertise (EEG / MoBI / ICA)</Th>
                    <Th align="center">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((u) => (
                    <tr
                      key={u.email}
                      onClick={() =>
                        navigate(`/admin/quality/user/${encodeURIComponent(u.email)}`)
                      }
                      style={rowStyle}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(8, 145, 178, 0.04)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>
                          {u.fname} {u.lname}
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{u.email}</div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        {u.isAdmin ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              background: "rgba(8, 145, 178, 0.1)",
                              color: "#0891b2",
                              border: "1px solid rgba(8, 145, 178, 0.4)",
                              borderRadius: 10,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                            }}
                          >
                            ★ Admin
                          </span>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: 12 }}>user</span>
                        )}
                      </td>
                      <td style={tdStyle}>{u.assoc || "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                        {u.totalLabels.toLocaleString()}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {u.uniqueComponents}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          textAlign: "right",
                          fontFamily:
                            '"JetBrains Mono", "SF Mono", Menlo, monospace',
                          fontWeight: 600,
                          color: u.weight < 1 ? "#d97706" : "#0a0e1a",
                        }}
                        title="Edit on the user profile page"
                      >
                        {(typeof u.weight === "number" ? u.weight : 1).toFixed(2)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", minWidth: 150 }}>
                        <AgreementBar pct={u.agreementPct} evaluated={u.evaluated} />
                      </td>
                      <td style={tdStyle}>
                        <ExpertiseChips ex={u.expertise} />
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={(e) => handleDelete(u, e)}
                          title="Delete this user"
                          style={{
                            background: "transparent",
                            border: "1px solid rgba(220, 38, 38, 0.45)",
                            color: "#dc2626",
                            padding: "5px 12px",
                            borderRadius: 6,
                            fontFamily: "Poppins, sans-serif",
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sortedUsers.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        style={{ padding: 30, textAlign: "center", color: "#6b7280" }}
                      >
                        No users match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ------------- Small helpers -----------------

const inputStyle = (width) => ({
  width,
  padding: "9px 14px",
  border: "1px solid rgba(8, 145, 178, 0.24)",
  borderRadius: 8,
  fontSize: 14,
  fontFamily: "Poppins, sans-serif",
  color: "#0a0e1a",
  background: "#fff",
  outline: "none",
});

const headerRowStyle = {
  background: "rgba(8, 145, 178, 0.04)",
  borderBottom: "1px solid rgba(8, 145, 178, 0.18)",
};

const rowStyle = {
  borderBottom: "1px solid rgba(8, 145, 178, 0.08)",
  cursor: "pointer",
  transition: "background 0.2s ease",
};

const tdStyle = {
  padding: "14px 16px",
  fontSize: 14,
};

const Th = ({ children, onClick, sortKey, dir, thisKey, align }) => {
  const isActive = thisKey && sortKey === thisKey;
  return (
    <th
      onClick={onClick}
      style={{
        padding: "12px 16px",
        textAlign: align || "left",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: isActive ? "#0891b2" : "#6b7280",
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
      }}
    >
      {children}
      {isActive && <span style={{ marginLeft: 4 }}>{dir === "asc" ? "↑" : "↓"}</span>}
    </th>
  );
};

const StatCell = ({ label, value }) => (
  <div
    style={{
      flex: "0 0 auto",
      padding: "12px 18px",
      border: "1px solid rgba(8, 145, 178, 0.2)",
      borderRadius: 10,
      background: "rgba(8, 145, 178, 0.04)",
      minWidth: 140,
    }}
  >
    <div
      style={{
        fontSize: 10,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "#6b7280",
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 22,
        fontWeight: 700,
        color: "#0a0e1a",
        marginTop: 2,
      }}
    >
      {value}
    </div>
  </div>
);

const AgreementBar = ({ pct, evaluated }) => {
  if (pct === null || pct === undefined || !evaluated) {
    return (
      <span style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>—</span>
    );
  }
  const color = pct >= 70 ? "#059669" : pct >= 40 ? "#d97706" : "#dc2626";
  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 4,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color }}>
        {pct.toFixed(1)}%
      </div>
      <div
        style={{
          width: 120,
          height: 4,
          background: "rgba(8, 145, 178, 0.08)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.min(100, pct)}%`,
            height: "100%",
            background: color,
          }}
        />
      </div>
      <div style={{ fontSize: 10, color: "#9ca3af" }}>
        on {evaluated} IC{evaluated === 1 ? "" : "s"}
      </div>
    </div>
  );
};

const ExpertiseChips = ({ ex }) => {
  if (!ex) return null;
  const chips = [
    { label: "EEG", val: ex.expEEG },
    { label: "MoBI", val: ex.expMoBI },
    { label: "ICA", val: ex.expICA },
  ].filter((c) => c.val);
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {chips.map((c) => (
        <span
          key={c.label}
          style={{
            padding: "3px 8px",
            background: "rgba(8, 145, 178, 0.08)",
            border: "1px solid rgba(8, 145, 178, 0.2)",
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            color: "#0891b2",
          }}
        >
          {c.label} {c.val}
        </span>
      ))}
      {chips.length === 0 && (
        <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
      )}
    </div>
  );
};

export default AdminQuality;
